import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';

const CosmicBackground = ({ children, variant = 'default' }) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Beautiful cosmic background images for different pages
  const backgroundImages = {
    default: 'https://images.unsplash.com/photo-1639215837470-cb6aa394fa02', // Starry night sky
    services: 'https://images.unsplash.com/photo-1590418150653-0fce5959b808', // Blue nebula
    about: 'https://images.unsplash.com/photo-1533294455009-a77b7557d2d1', // Lunar phases
    contact: 'https://images.unsplash.com/photo-1444703686981-a3abbc4d4fe3', // Silhouette under stars
    pricing: 'https://images.unsplash.com/photo-1503490837623-f01e6aed83d4' // Moon
  };

  // Lightweight star field - keep some magic but optimize for mobile
  const generateStars = useMemo(() => {
    const starCount = isMobile ? 6 : 12; // Reduced but still magical
    
    return [...Array(starCount)].map((_, i) => (
      <motion.div
        key={i}
        className={`absolute rounded-full ${
          i % 3 === 0 ? 'w-1 h-1 bg-yellow-200' : 
          i % 3 === 1 ? 'w-0.5 h-0.5 bg-white' : 
          'w-1.5 h-1.5 bg-yellow-300'
        } opacity-70`}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{
          opacity: [0.3, 0.8, 0.3],
          scale: [0.8, 1.2, 0.8]
        }}
        transition={{
          duration: 3 + Math.random() * 2,
          repeat: Infinity,
          delay: Math.random() * 2,
          ease: "easeInOut"
        }}
      />
    ));
  }, [isMobile]);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Beautiful static cosmic background with gradient overlay */}
      <div className="fixed inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImages[variant] || backgroundImages.default})`,
          }}
        />
        
        {/* Cosmic gradient overlay for better text contrast and cosmic feel */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-purple-900/70 to-indigo-950/80" />
        
        {/* Additional overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/40" />
      </div>

      {/* Lightweight twinkling stars - keep the magic! */}
      <div className="fixed inset-0 pointer-events-none">
        {generateStars}
      </div>

      {/* Elegant floating moon - with subtle animation */}
      <motion.div 
        className={`fixed ${isMobile ? 'top-16 right-4' : 'top-20 right-20'} opacity-80`}
        animate={{
          y: [0, -10, 0],
          opacity: [0.6, 0.9, 0.6]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <div className={`${isMobile ? 'w-8 h-8' : 'w-12 h-12'} bg-gradient-to-br from-yellow-200 to-yellow-400 rounded-full shadow-lg`} 
             style={{
               filter: 'drop-shadow(0 0 10px rgba(255, 255, 0, 0.3))'
             }}
        />
      </motion.div>

      {/* Content container */}
      <div className="relative z-20">
        {children}
      </div>
    </div>
  );
};

export default CosmicBackground;